import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, from, filter, firstValueFrom } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { selectAuthState } from 'pl-core-services';
import { take } from 'rxjs/operators';
import { AuthSyncService } from './auth-sync.service';
import { isValidReturnUrl } from './return-url.utils';

@Injectable({ providedIn: 'root' })
export class AuthSyncGuard implements CanActivate {
  private readonly DEBUG = environment.debug;

  private readonly SYNC_TIMEOUT = 500;

  private readonly STATE_SETTLE_DELAY = 100;

  private readonly RETURN_URL_KEY = 'auth_return_url';

  private log(message: string, data?: any) {
    if (this.DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[AuthSyncGuard] ${message}`, data || '');
    }
  }

  constructor(
    private store: Store,
    private router: Router,
    private oidcSecurityService: OidcSecurityService,
    private authSyncService: AuthSyncService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return from(this.handleAuthSync(state));
  }

  private async handleAuthSync(
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const APP_NAMESPACE = (window as any).AUTH_CONSTANTS?.APP_NAMESPACE || 'te';

    this.log('Starting auth sync process');

    // ==================================================================================
    // EARLY EXIT: Redirect recently-logged-out users to /auth/logged-out
    // ==================================================================================
    //
    // WITHOUT this block, the guard falls through to authorize() below, which
    // redirects to Sirius. If the Sirius session is still alive (cookie not yet
    // destroyed), authorize() triggers instant SSO and the user is silently
    // logged back in — defeating the logout. This block prevents authorize()
    // from ever being called after a logout.
    //
    // WHY "falling through" is NOT the same as this early exit:
    // authorize() is fire-and-forget — it kicks off an async chain that
    // ends with document.location.href being set to the Sirius authorize
    // endpoint. The guard returns false after calling it, but that only
    // blocks the Angular route momentarily. The redirect still fires
    // (it cannot be cancelled), the browser navigates to Sirius, and if
    // the Sirius session is still alive, SSO completes silently — the user
    // is re-authenticated without ever seeing the logged-out page.
    // This block returns createUrlTree(['/auth/logged-out']) INSTEAD, so
    // authorize() is never called and the user stays in the SPA.
    //
    // DUAL-CONDITION GUARD: Requires BOTH isRecentlyLoggedOut() AND no token.
    // This prevents false positives during the brief window where the logout flag is
    // set but a token still exists.
    //
    // STALE LOCK CLEANUP: Removes SSO leadership keys to prevent future login issues.
    // ==================================================================================
    if (this.authSyncService.isRecentlyLoggedOut()) {
      const token = await firstValueFrom(
        this.oidcSecurityService.getAccessToken().pipe(take(1))
      );
      if (!token) {
        // Before showing logout screen, check if a valid server session exists.
        // This handles cross-subdomain login: user logs out here, logs in on
        // another subdomain (e.g., room.presence.com), then navigates back.
        // The localStorage logout flag persists (per-origin) but the server
        // session is now valid via the shared .presence.com cookie.
        const hasValidSession = await this.authSyncService.isSessionValid();
        if (hasValidSession) {
          this.log(
            'Valid server session found despite logout flag - clearing flag and proceeding'
          );
          this.authSyncService.clearLogoutFlag();
          // Fall through to normal auth flow below
        } else {
          this.log(
            'User recently logged out with no token - redirecting to logged-out page'
          );
          localStorage.removeItem(`${APP_NAMESPACE}_returning_from_sso`);
          localStorage.removeItem(`${APP_NAMESPACE}_leader_at_sso`);
          return this.router.createUrlTree(['/auth/logged-out']);
        }
      }
    }

    // Fastest path: check if we already have a valid token
    const initialToken = await firstValueFrom(
      this.oidcSecurityService.getAccessToken().pipe(take(1))
    );

    if (initialToken) {
      this.log('Found existing valid token');
      return true;
    }

    try {
      // Second priority: Check store state (still fast)
      const authState = await firstValueFrom(
        this.store.select(selectAuthState).pipe(
          filter((st) => st !== 'pending'),
          take(1)
        )
      );

      if (authState === 'success') {
        this.log('Auth state already success');
        return true;
      }
    } catch (error) {
      this.log('Error fetching auth state', error);
    }

    this.log('No existing token or auth state, attempting sync');

    // Try to sync auth state from other tabs
    try {
      const synced = await this.authSyncService.waitForAuthState(
        this.SYNC_TIMEOUT
      );

      if (synced) {
        this.log('Auth sync successful, waiting for state to settle');

        // Add a small delay to ensure OIDC internal state is updated
        await new Promise<void>((resolve) => {
          setTimeout(resolve, this.STATE_SETTLE_DELAY);
        });

        const syncedToken = await firstValueFrom(
          this.oidcSecurityService.getAccessToken().pipe(take(1))
        );
        if (syncedToken) {
          this.log('Token obtained after sync');

          // Double check auth state after sync
          const authState = await firstValueFrom(
            this.store.select(selectAuthState).pipe(
              filter((st) => st !== 'pending'),
              take(1)
            )
          );

          if (authState === 'success') {
            this.log('Auth state verified after sync');
            // Force a check but don't wait for it
            // CRITICAL: Trigger index.html restoration by reading tab ID
            // See auth-sync.service.ts for full explanation
            const tabIdKey = `${
              (window as any).AUTH_CONSTANTS?.APP_NAMESPACE || 'te'
            }_tab_id`;
            const preservedTabId = sessionStorage.getItem(tabIdKey);
            this.log(
              `[TAB PROTECTION - GUARD] Preserved tab ID before checkAuth: ${preservedTabId}`
            );

            this.oidcSecurityService.checkAuth().subscribe({
              next: () => {
                const currentTabId = sessionStorage.getItem(tabIdKey);
                if (preservedTabId && !currentTabId) {
                  sessionStorage.setItem(tabIdKey, preservedTabId);
                  this.log(
                    `[TAB PROTECTION - GUARD] Restored tab ID after checkAuth: ${preservedTabId}`
                  );
                } else if (currentTabId) {
                  this.log(
                    `[TAB PROTECTION - GUARD] Tab ID already present: ${currentTabId}`
                  );
                }
              },
              error: () => {
                const currentTabId = sessionStorage.getItem(tabIdKey);
                if (preservedTabId && !currentTabId) {
                  sessionStorage.setItem(tabIdKey, preservedTabId);
                  this.log(
                    `[TAB PROTECTION - GUARD] Restored tab ID after error: ${preservedTabId}`
                  );
                }
              },
            });
            return true;
          }
        }
      }

      // CRITICAL: Only ONE tab should call authorize() to prevent multiple simultaneous OAuth flows
      // The approach I'm taking here is a distributed lock (comp and swap) via localStorage to have
      // as close a guarantee of atomicitiy as we can realistically manage in a browser context
      this.log('Auth sync unsuccessful, attempting to claim SSO handling');

      // ... now do the comp and swap technique...
      const tabId = sessionStorage.getItem(`${APP_NAMESPACE}_tab_id`);
      const myClaimValue = JSON.stringify({
        tabId,
        timestamp: Date.now(),
      });

      // Check if someone already has claim
      const existingClaim = localStorage.getItem(
        `${APP_NAMESPACE}_leader_at_sso`
      );

      if (!existingClaim) {
        // No one has claimed it yet - try to claim it
        localStorage.setItem(`${APP_NAMESPACE}_leader_at_sso`, myClaimValue);
        localStorage.setItem(`${APP_NAMESPACE}_returning_from_sso`, 'true');

        // Yield to the event loop to allow other tabs' writes to propagate
        // Because CodeRabbit is ruthless, I've written proper CAS with write, wait, verify
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 50);
        });

        // Verify our claim is still present (wasn't overwritten by another tab)
        const currentValue = localStorage.getItem(
          `${APP_NAMESPACE}_leader_at_sso`
        );

        // Only proceed if our exact claim value is still there...
        if (currentValue === myClaimValue) {
          this.log(
            'Successfully claimed SSO handling (CAS verified), initiating authorize()'
          );
          // Store the requested URL for post-authentication redirect (leader tab only)
          if (state?.url && isValidReturnUrl(state.url)) {
            sessionStorage.setItem(this.RETURN_URL_KEY, state.url);
            this.log('Stored return URL for leader tab', state.url);
          }
          this.oidcSecurityService.authorize();
          return false;
        }
        this.log(
          'Another tab claimed SSO - our claim was overwritten, waiting for them'
        );
        // Fall through to wait logic below
      }

      // If we get here, another tab claimed SSO handling - wait for them
      const leaderAtSSO = localStorage.getItem(
        `${APP_NAMESPACE}_leader_at_sso`
      );
      if (leaderAtSSO) {
        try {
          const ssoInfo = JSON.parse(leaderAtSSO);
          const waitTime = Date.now() - ssoInfo.timestamp;
          const remainingWait = Math.max(10000 - waitTime, 2000); // Wait at least 2s, up to 10s

          this.log(
            `Tab (${ssoInfo.tabId}) is handling SSO - waiting ${remainingWait}ms`
          );

          // NOTE: Taking a slightly different approach here in therapy-essentials, but we can add this poll to
          // other apps, if necessary...this just optimizes things a bit without loosing any security, speeding
          // up the very specific multi-tab simultaneous loading in same ms case - note that this works
          // here because by and large the components in therapy-essentials ALREADY do a good job with
          // ensuring they aren't racy - I'd be cautious about doing this in other apps where those components
          // may have known issues.
          const pollInterval = 100; // Check every 100ms
          const maxWait = remainingWait;
          const startTime = Date.now();

          // eslint-disable-next-line no-await-in-loop -- Intentional: polling requires sequential checks
          while (Date.now() - startTime < maxWait) {
            // eslint-disable-next-line no-await-in-loop -- Intentional: polling requires sequential checks
            const token = await firstValueFrom(
              this.oidcSecurityService.getAccessToken().pipe(take(1))
            );
            if (token) {
              this.log('SSO completed, token now available');
              return true;
            }
            // eslint-disable-next-line no-await-in-loop -- Intentional: polling requires sequential checks
            await new Promise((resolve) => {
              setTimeout(() => resolve(undefined), pollInterval);
            });
          }

          this.log('SSO timeout - no token received');
        } catch (e) {
          this.log('Error parsing leader_at_sso');
        }
      }

      return false;
    } catch (error) {
      this.log('Error during auth sync process', error);

      // CRITICAL: Use same dist lock logic as main path!
      const tabId = sessionStorage.getItem(`${APP_NAMESPACE}_tab_id`);
      const myClaimValue = JSON.stringify({
        tabId,
        timestamp: Date.now(),
      });

      const existingClaim = localStorage.getItem(
        `${APP_NAMESPACE}_leader_at_sso`
      );

      if (!existingClaim) {
        localStorage.setItem(`${APP_NAMESPACE}_leader_at_sso`, myClaimValue);
        localStorage.setItem(`${APP_NAMESPACE}_returning_from_sso`, 'true');

        // Yield to event loop to allow other tabs' writes to propagate...
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 50);
        });

        const currentValue = localStorage.getItem(
          `${APP_NAMESPACE}_leader_at_sso`
        );

        // Only proceed if our exact claim value is still there
        if (currentValue === myClaimValue) {
          this.log(
            'Error path: Successfully claimed SSO handling (CAS verified), initiating authorize()'
          );
          // Store the requested URL for post-authentication redirect (leader tab only)
          if (state?.url && isValidReturnUrl(state.url)) {
            sessionStorage.setItem(this.RETURN_URL_KEY, state.url);
            this.log(
              'Stored return URL for leader tab (error path)',
              state.url
            );
          }
          this.oidcSecurityService.authorize();
        } else {
          this.log(
            'Error path: Another tab claimed SSO - our claim was overwritten'
          );
        }
      } else {
        this.log('Error path: Another tab already handling SSO');
      }

      return false;
    }
  }
}
